import { useEffect, useRef, useState } from "react";
import ErrorMessage from "@/app/(components)/generics/messages/ErrorMessage/ErrorMessage";
import { UStyles } from "@/app/utils/UStyles";
import defaultInputStyles from "../../styles/DefaultInput.module.css";
import styles from "./Input.module.css";

export enum TextInputType {
  TEXT = "text",
  TEXTAREA = "textarea",
  EMAIL = "email",
  PASSWORD = "password",
  URL = "url",
  NUMBER = "number",
  TEL = "tel",
  DATE = "date",
}

export type InputProps<T extends Record<string, any>> = {
  type?: TextInputType;
  placeholder?: string;
  label?: string;
  required?: boolean;
  data: T;
  dataKey?: keyof T & string;
  errorMessage?: string;
  charsLimit?: {
    min?: number;
    max?: number;
    showCounter?: boolean;
  };
  textareaRows?: number;
  textareaMaxHeight?: string;
  onClick?: (data: T) => void;
  onChange?: (data: T) => void;
  onBlur?: (data: T) => void;
  autoFocus?: boolean;
  hideZero?: boolean;
  disabled?: boolean;
  className?: string;
};

export function Input<T extends Record<string, any>>(props: InputProps<T>) {
  let {
    type = TextInputType.TEXT,
    placeholder = "",
    label,
    required = false,
    data,
    dataKey,
    errorMessage = "",
    charsLimit,
    textareaRows = 3,
    textareaMaxHeight,
    onClick = () => {},
    onChange,
    onBlur,
    autoFocus,
    hideZero,
    disabled = false,
    className,
  } = props;

  if (!dataKey) {
    const [key] = Object.entries(data)[0];
    dataKey = key as keyof T & string;
  }

  const [valueLen, setValueLen] = useState(String(data[dataKey] || "").length);

  // A limited textarea always shows its counter; single-line inputs only when asked.
  const showCounter =
    !!charsLimit &&
    (charsLimit.showCounter || (type === TextInputType.TEXTAREA && charsLimit.max !== undefined));

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      if (autoFocus) {
        textarea.focus();
      }
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [autoFocus]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target) {
      const input = e.target;
      const targetData = data as Record<string, unknown>;

      if (type === TextInputType.NUMBER) {
        targetData[dataKey!] = parseInt(input.value || "0", 10);
        if (hideZero && targetData[dataKey!] === 0) {
          input.value = "";
        } else {
          input.value = String(targetData[dataKey!]);
        }
      } else {
        targetData[dataKey!] = input.value || "";
      }

      if (onChange) {
        onChange(data);
      }

      if (showCounter) {
        setValueLen(String(targetData[dataKey!]).length);
      }
    }
  };

  const buildInput = () => {
    switch (type) {
      case TextInputType.TEXTAREA:
        return (
          <textarea
            ref={textareaRef}
            disabled={disabled}
            className={defaultInputStyles.input}
            name={dataKey}
            minLength={charsLimit?.min}
            maxLength={charsLimit?.max}
            placeholder={placeholder}
            defaultValue={String(data[dataKey!] || "")}
            onClick={() => onClick(data)}
            onChange={handleChange}
            onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "auto";
              target.style.height = `${target.scrollHeight}px`;
              handleChange(e as unknown as React.ChangeEvent<HTMLTextAreaElement>);
            }}
            onBlur={() => onBlur?.(data)}
            style={{
              overflow: textareaMaxHeight ? "auto" : "hidden",
              resize: "none",
              maxHeight: textareaMaxHeight,
            }}
            rows={textareaRows}
          />
        );

      case TextInputType.URL:
        return (
          <input
            disabled={disabled}
            className={defaultInputStyles.input}
            type={type}
            pattern={"https://.*"}
            name={dataKey}
            minLength={charsLimit?.min}
            maxLength={charsLimit?.max}
            placeholder={placeholder ? placeholder : "https://..."}
            defaultValue={String(data[dataKey!] || "")}
            onClick={() => onClick(data)}
            onChange={handleChange}
          />
        );

      case TextInputType.NUMBER:
        return (
          <input
            disabled={disabled}
            className={defaultInputStyles.input}
            type={type}
            name={dataKey}
            min={charsLimit?.min}
            max={charsLimit?.max}
            placeholder={placeholder}
            defaultValue={String(data[dataKey!] || "")}
            onClick={() => onClick(data)}
            onChange={handleChange}
          />
        );

      case TextInputType.TEL:
        return (
          <input
            disabled={disabled}
            className={defaultInputStyles.input}
            type={"number"}
            name={dataKey}
            min={charsLimit?.min}
            max={charsLimit?.max}
            placeholder={placeholder}
            defaultValue={String(data[dataKey!] || "")}
            onClick={() => onClick(data)}
            onChange={handleChange}
          />
        );
    }

    return (
      <input
        disabled={disabled}
        className={defaultInputStyles.input}
        type={type}
        name={dataKey}
        minLength={charsLimit?.min}
        maxLength={charsLimit?.max}
        placeholder={placeholder}
        defaultValue={String(data[dataKey!] || "")}
        onClick={() => onClick(data)}
        onChange={handleChange}
        onBlur={() => onBlur?.(data)}
      />
    );
  };

  return (
    <div className={UStyles.classNames(styles.container, className)}>
      {label && (
        <label className={styles.label} htmlFor={dataKey}>
          {label}
          {required && <span className={styles.required}> *</span>}
        </label>
      )}
      {buildInput()}

      <div className={styles.content}>
        {errorMessage ? (
          <div className={UStyles.classNames(styles.errorMessage, defaultInputStyles.errorMessage)}>
            <ErrorMessage message={errorMessage} />
          </div>
        ) : (
          <div></div>
        )}

        {showCounter && charsLimit?.max !== undefined && (
          <div className={styles.charsLimit}>
            <p className={styles.charsLimitText}>
              {valueLen}/{charsLimit.max}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
